import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { X, AlertTriangle, Calendar, Clock, TrendingUp } from 'lucide-react';
import { addInventoryEvent } from '../../store/slices/inventorySlice';

const CorrectDeficitModal = ({ isOpen, onClose, onSuccess, item }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    cantidad: '',
    fecha_evento: new Date().toISOString().split('T')[0],
    hora_evento: new Date().toTimeString().slice(0, 5),
    observaciones: ''
  });

  const deficit = item ? Math.abs(item.cantidad) : 0;
  const minQuantity = deficit;

  useEffect(() => {
    if (!isOpen) {
      setFormData({
        cantidad: '',
        fecha_evento: new Date().toISOString().split('T')[0],
        hora_evento: new Date().toTimeString().slice(0, 5),
        observaciones: ''
      });
      return;
    }

    if (item && item.cantidad < 0) {
      setFormData(prev => ({
        ...prev,
        cantidad: Math.abs(item.cantidad).toString(),
        observaciones: `Corrección de déficit - Entrada no registrada`
      }));
    }
  }, [isOpen, item]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const cantidad = parseInt(formData.cantidad);
    
    if (!cantidad || cantidad <= 0) {
      alert('La cantidad debe ser mayor que cero');
      return;
    }

    if (cantidad < minQuantity) {
      alert(`La cantidad debe ser al menos ${minQuantity} para corregir el déficit`);
      return;
    }

    if (!formData.observaciones || formData.observaciones.trim() === '') {
      alert('Por favor ingrese una observación explicando la corrección');
      return;
    }

    setLoading(true);

    try {
      const fechaHora = `${formData.fecha_evento}T${formData.hora_evento}:00`;
      
      const dataToSubmit = {
        producto_id: item.producto_id,
        tipo_evento: 'AGREGAR',
        cantidad: cantidad,
        fecha_evento: fechaHora,
        ubicacion: item.ubicacion,
        observaciones: formData.observaciones
      };

      await dispatch(addInventoryEvent(dataToSubmit)).unwrap();
      
      const newStock = item.cantidad + cantidad;
      alert(`Déficit corregido exitosamente. Stock actualizado a ${newStock} cajas.`);
      
      setFormData({
        cantidad: '',
        fecha_evento: new Date().toISOString().split('T')[0],
        hora_evento: new Date().toTimeString().slice(0, 5),
        observaciones: ''
      });

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      console.error('Error correcting deficit:', error);
      alert(error.message || 'Error al corregir el déficit');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !item) return null;

  const previewStock = formData.cantidad ? item.cantidad + parseInt(formData.cantidad) : item.cantidad;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-header-content">
            <div>
              <h2 className="modal-title">Corregir Déficit de Inventario</h2>
              <p className="modal-subtitle">
                {item.productos?.nombre || 'Producto'}
              </p>
            </div>
          </div>
          <button className="modal-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="deficit-info-box">
          <AlertTriangle size={24} strokeWidth={2.5} />
          <div>
            <p className="deficit-info-title">
              El producto tiene un déficit de <strong>{deficit} unidades</strong>
            </p>
            <p className="deficit-info-text">
              Esto indica que faltan registrar <strong>{deficit} unidades</strong> de entrada. 
              Use este formulario para agregar las entradas faltantes y normalizar el inventario.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label htmlFor="cantidad">
                Cantidad a Agregar <span className="required">*</span>
                <span className="label-hint">(mínimo: {minQuantity})</span>
              </label>
              <input
                type="number"
                id="cantidad"
                name="cantidad"
                value={formData.cantidad}
                onChange={handleChange}
                min={minQuantity}
                required
                className="form-input"
                placeholder={`Mínimo ${minQuantity} cajas`}
              />
              <p className="form-hint">
                Ingrese la cantidad de cajas que faltaron registrar. 
                Puede ser mayor si desea dejar stock positivo.
              </p>
            </div>

            {formData.cantidad && (
              <div className="stock-preview">
                <TrendingUp size={18} />
                <div className="stock-preview-content">
                  <div className="stock-preview-row">
                    <span>Stock actual:</span>
                    <span className="stock-deficit">{item.cantidad}</span>
                  </div>
                  <div className="stock-preview-row">
                    <span>Cantidad a agregar:</span>
                    <span className="stock-add">+{formData.cantidad}</span>
                  </div>
                  <div className="stock-preview-row stock-preview-total">
                    <span>Stock resultante:</span>
                    <span className={previewStock >= 0 ? 'stock-positive' : 'stock-deficit'}>
                      {previewStock}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="fecha_evento">
                  <Calendar size={16} />
                  Fecha de Entrada Real <span className="required">*</span>
                </label>
                <input
                  type="date"
                  id="fecha_evento"
                  name="fecha_evento"
                  value={formData.fecha_evento}
                  onChange={handleChange}
                  max={new Date().toISOString().split('T')[0]}
                  required
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="hora_evento">
                  <Clock size={16} />
                  Hora
                </label>
                <input
                  type="time"
                  id="hora_evento"
                  name="hora_evento"
                  value={formData.hora_evento}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="observaciones">
                Observaciones <span className="required">*</span>
              </label>
              <textarea
                id="observaciones"
                name="observaciones"
                value={formData.observaciones}
                onChange={handleChange}
                required
                rows="3"
                className="form-input"
                placeholder="Ej: Entrada no registrada del 15/04/2026 - Lote recibido de campo"
              />
              <p className="form-hint">
                Documente la razón de la corrección para mantener trazabilidad.
              </p>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Corrigiendo...' : 'Agregar Entrada Faltante'}
            </button>
          </div>
        </form>
      </div>

      <style jsx>{`
        .deficit-info-box {
          display: flex;
          gap: var(--spacing-3);
          padding: var(--spacing-4);
          background: linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%);
          border: 2px solid #F59E0B;
          border-left: 6px solid #D97706;
          border-radius: var(--radius-lg);
          margin-bottom: var(--spacing-4);
          color: #78350F;
          box-shadow: 0 4px 6px -1px rgba(245, 158, 11, 0.1), 0 2px 4px -1px rgba(245, 158, 11, 0.06);
        }

        .deficit-info-box > svg {
          flex-shrink: 0;
          color: #D97706;
          margin-top: 2px;
        }

        .deficit-info-title {
          font-weight: 700;
          margin-bottom: var(--spacing-2);
          color: #92400E;
          font-size: var(--font-size-base);
        }

        .deficit-info-text {
          font-size: var(--font-size-sm);
          line-height: 1.6;
          color: #92400E;
        }

        .label-hint {
          font-size: var(--font-size-sm);
          color: var(--gray-500);
          font-weight: normal;
          margin-left: var(--spacing-2);
        }

        .stock-preview {
          display: flex;
          gap: var(--spacing-3);
          padding: var(--spacing-4);
          background-color: #F0F9FF;
          border: 1px solid #BAE6FD;
          border-radius: var(--radius-lg);
          margin-bottom: var(--spacing-4);
        }

        .stock-preview-content {
          flex: 1;
        }

        .stock-preview-row {
          display: flex;
          justify-content: space-between;
          padding: var(--spacing-2) 0;
          font-size: var(--font-size-sm);
        }

        .stock-preview-total {
          border-top: 2px solid #0EA5E9;
          margin-top: var(--spacing-2);
          padding-top: var(--spacing-3);
          font-weight: 600;
          font-size: var(--font-size-base);
        }

        .stock-deficit {
          color: #DC2626;
          font-weight: 700;
        }

        .stock-add {
          color: #10B981;
          font-weight: 600;
        }

        .stock-positive {
          color: #10B981;
          font-weight: 700;
        }

        .required {
          color: #DC2626;
        }

        .form-hint {
          font-size: var(--font-size-sm);
          color: var(--gray-500);
          margin-top: var(--spacing-1);
        }
      `}</style>
    </div>
  );
};

export default CorrectDeficitModal;
